import { Status } from "./live-codec.js";
import { LiveTimings } from "./live-metrics.js";
import { createViewerWorker } from "./viewer-worker.js";

/** Route-independent integration. loadMap must honor signal; applyEntities should only update scene state. */
export class LiveController {
    constructor({ loadMap, applyEntities, onMapReady = () => {}, onStatus = () => {}, onMetrics = () => {}, onReset = () => {}, onVoice = () => {}, onViewerAccess = () => {}, passive = false, workerFactory = () => createViewerWorker('live-worker') }) {
        this.passive = passive;
        this.loadMap = loadMap; this.applyEntities = applyEntities; this.onStatus = onStatus; this.onReset = onReset; this.onVoice = onVoice; this.onViewerAccess = onViewerAccess;
        this.onMapReady = onMapReady; this.assets = null; this.target = null;
        this.worker = workerFactory(); this.voiceEnabled = false; this.generation = 0; this.epoch = null; this.map = null;
        this.worker.onmessage = event => this.receive(event.data);
        this.worker.onerror = () => this.onStatus({ status: Status.Disconnected, diagnostic: "Live stream worker failed" });
        this.onMetrics = onMetrics; this.timings = new LiveTimings(["deliveryMs"]); this.workerMetrics = null;
    }
    selectServer(serverId) {
        if (!Number.isSafeInteger(serverId) || serverId <= 0) throw new Error("Invalid server ID");
        this.clearMap(); this.generation++;
        this.timings.reset(); this.workerMetrics = null;
        const url = new URL(`/stream/${this.passive ? 'embed/' : ''}${serverId}`, location.href);
        url.protocol = url.protocol === "https:" ? "wss:" : "ws:";
        this.worker.postMessage({ type: "connect", generation: this.generation, url: url.href, voiceEnabled: this.voiceEnabled });
    }
    sendPosition(position, angles) {
        if (this.passive) return;
        if (this.epoch !== null && this.map) this.worker.postMessage({ type: 'viewer-position', generation: this.generation, epoch: this.epoch, position, angles });
    }
    sendChat(text) {
        if (this.passive) return;
        if (this.epoch !== null && this.map) this.worker.postMessage({ type: 'viewer-chat', generation: this.generation, epoch: this.epoch, text });
    }
    setVoiceEnabled(enabled) {
        if (this.passive) return;
        this.voiceEnabled = !!enabled;
        this.worker.postMessage({ type: 'voice-enable', generation: this.generation, enabled: this.voiceEnabled });
    }
    async receive(message) {
        if (message.generation !== this.generation) return;
        if (message.type === 'viewer-access') { this.onViewerAccess(message.enabled); return; }
        if (message.type === 'voice') {
            try {
                if (message.epoch === this.epoch && this.map && performance.timeOrigin + performance.now() - message.sentAt < 200) this.onVoice(message.payload);
            } finally { this.worker.postMessage({ type: 'voice-consumed', generation: message.generation }); }
            return;
        }
        if (message.type === "entities") {
            try {
                if (message.epoch === this.epoch && this.map) {
                    this.timings.record("deliveryMs", performance.timeOrigin + performance.now() - message.sentAt);
                    await this.applyEntities(message);
                    if (message.generation === this.generation && message.epoch === this.epoch && message.metrics) { this.workerMetrics = message.metrics; this.onMetrics(this.diagnostics()); }
                }
            } catch (error) {
                this.onStatus({ status: Status.Stale, diagnostic: error.message });
                this.disconnect();
            } finally { this.worker.postMessage({ type: "consumed", generation: message.generation }); }
            return;
        }
        if (message.type !== "status") return;
        this.onStatus(message);
        if (message.replaced) {
            this.clearMap();
            return;
        }
        if (message.status === Status.MapChanging) {
            // MapEnd keeps its epoch: invalidate the scene and pending assets immediately.
            this.clearMap();
            return;
        }
        if (!message.map?.id) {
            if (message.epoch !== this.epoch) {
                // A publisher reconnect can announce its new epoch before map metadata.
                // Assets belong to map content, so keep their preparation across that gap.
                this.resetScene(); this.epoch = message.epoch; this.target = null;
            }
            return;
        }
        const key = JSON.stringify([message.map.name, message.map.id, message.map.revision, message.map.assetSet]);
        // Reconnect with the same map/epoch still needs Ready, but does not reload assets.
        if (message.epoch === this.epoch && this.map === key) {
            if (message.status === Status.AwaitingSnapshot || message.status === Status.Live || message.status === Status.Stale)
                this.worker.postMessage({ type: "ready", generation: this.generation, epoch: this.epoch });
            return;
        }
        if (this.target?.epoch !== message.epoch || this.target?.key !== key) this.resetScene();
        this.target = { epoch: message.epoch, key, message };
        if (this.assets?.key !== key) {
            this.assets?.abort.abort();
            const assets = { key, abort: new AbortController() };
            this.assets = assets;
            assets.promise = this.prepareMap(assets, message);
        }
        const assets = this.assets;
        await assets.promise;
        if (assets !== this.assets || assets.abort.signal.aborted || this.target?.message !== message) return;
        if (assets.error) {
            // Retry is explicit: repeated publisher statuses must not restart failed downloads.
            this.onStatus({ ...message, status: Status.MapUnavailable, diagnostic: assets.error.message });
            return;
        }
        try {
            this.onMapReady(assets.result);
            this.epoch = message.epoch; this.map = key;
            this.worker.postMessage({ type: "ready", generation: this.generation, epoch: this.epoch });
        } catch (error) {
            assets.error = error;
            this.onStatus({ ...message, status: Status.MapUnavailable, diagnostic: error.message });
        }
    }
    async prepareMap(assets, message) {
        try {
            assets.result = await this.loadMap(message.map, { signal: assets.abort.signal, epoch: message.epoch });
        } catch (error) { assets.error = error; }
        finally { assets.complete = true; }
    }
    get loadingMap() { return !!this.assets && !this.assets.complete; }
    resetScene() {
        this.onViewerAccess(false); this.onReset(); this.epoch = null; this.map = null;
    }
    clearMap() {
        this.resetScene(); this.assets?.abort.abort(); this.assets = null; this.target = null;
    }
    disconnect() {
        this.clearMap(); this.worker.postMessage({ type: "disconnect", generation: this.generation });
        this.generation++;
        this.timings.reset(); this.workerMetrics = null;
    }
    diagnostics() { return { worker: this.workerMetrics, delivery: this.timings.summary() }; }
    dispose() { this.disconnect(); this.worker.terminate(); }
}

/** Assumed-page integration. loadMap returns the loaded map's asset-worker RPC function. */
export class LiveViewer {
    constructor({ renderer, loadMap, onStatus, onMetrics = () => {}, onEntities = () => {}, onReset = () => {}, onVoice = () => {}, onViewerAccess = () => {}, passive = false, workerFactory }) {
        this.renderer = renderer;
        this.controller = new LiveController({
            workerFactory, passive, onStatus, onVoice, onViewerAccess, onMetrics: () => onMetrics(this.diagnostics()),
            onReset: () => { this.scene?.dispose(); this.scene = null; onReset(); },
            loadMap: async (map, context) => {
                const rpc = await loadMap(map, context);
                if (context.signal.aborted) return;
                if (typeof rpc !== "function") throw new Error("Map loader must return its asset RPC function");
                return rpc;
            },
            onMapReady: rpc => { this.scene = renderer.startLive(rpc, { audio: !passive }); },
            applyEntities: message => { const result = this.scene?.apply(message); onEntities(message); return result; },
        });
    }
    selectServer(serverId) { this.controller.selectServer(serverId); }
    disconnect() { this.controller.disconnect(); }
    diagnostics() { return { ...this.controller.diagnostics(), scene: this.scene?.diagnostics() ?? null }; }
    dispose() { this.controller.dispose(); }
}
